import { ClosedEndedStatement } from 'types/interface';
import {
   Bar,
   BarChart,
   CartesianGrid,
   Legend,
   Tooltip,
   XAxis,
   YAxis,
} from 'recharts';
import { getShadeColors } from '../../pages/InvestmentsView';
import { formatNumber } from '../../lib/currency';
import CylinderBarShape, { getCylinderPosition } from './CylinderBarShape';

const X_AXIS_MAX_LINE_LENGTH = 14;
const CYLINDER_SHELL_COLOR = 'hsl(220, 9%, 46%)';
const STACK_KEYS = ['capitalCalled', 'unfunded'] as const;

type AxisTickProps = {
   x?: number;
   y?: number;
   payload?: {
      value?: string;
   };
};

type TooltipContentProps = {
   active?: boolean;
   payload?: Array<{
      payload?: ClosedEndedStatement;
   }>;
};

type InvestmentChartDatum = ClosedEndedStatement & {
   capitalCalled: number;
   unfunded: number;
};

function wrapLabel(value: string, maxLineLength: number) {
   const words = value.split(' ');
   const lines: string[] = [];
   let currentLine = '';

   words.forEach(word => {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;

      if (nextLine.length <= maxLineLength || !currentLine) {
         currentLine = nextLine;
      } else {
         lines.push(currentLine);
         currentLine = word;
      }
   });

   if (currentLine) {
      lines.push(currentLine);
   }

   return lines;
}

function WrappedXAxisTick({ x = 0, y = 0, payload }: AxisTickProps) {
   const value = String(payload?.value ?? '');
   const lines = wrapLabel(value, X_AXIS_MAX_LINE_LENGTH);

   return (
      <g transform={`translate(${x},${y + 10})`}>
         <text
            x={0}
            y={0}
            textAnchor="middle"
            dominantBaseline="hanging"
            fill="#666"
         >
            {lines.map((line, index) => (
               <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 0 : 14}>
                  {line}
               </tspan>
            ))}
         </text>
      </g>
   );
}

function formatCurrency(value: number) {
   return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
   });
}

function InvestmentsTooltipContent({ active, payload }: TooltipContentProps) {
   if (!active || !payload?.length || !payload[0]?.payload) {
      return null;
   }

   const investment = payload[0].payload;
   const commitment = Number(investment.totalCommitment ?? 0);
   const called = Math.max(
      0,
      Math.min(Number(investment.capitalCalled ?? 0), commitment),
   );
   const unfunded = Math.max(0, commitment - called);
   const fundName = String(investment.account?.fund?.name ?? 'Investment');
   const accountName = String(investment.account?.name ?? '');

   return (
      <div className="rounded border border-gray-200 bg-white px-3 py-2 shadow-sm">
         <div className="mb-1 text-sm font-semibold text-gray-800">
            {fundName}
         </div>
         {accountName && (
            <div className="mb-2 text-xs text-gray-500">{accountName}</div>
         )}
         <div className="text-sm text-gray-700">
            Commitment: {formatCurrency(commitment)}
         </div>
         <div className="text-sm text-gray-700">
            Called: {formatCurrency(called)}
         </div>
         <div className="text-sm text-gray-700">
            Unfunded: {formatCurrency(unfunded)}
         </div>
      </div>
   );
}

interface Props {
   investments: ClosedEndedStatement[];
}

export default function InvestmentsBarSummary({ investments }: Props) {
   const [calledColor] = getShadeColors(1);
   const chartData: InvestmentChartDatum[] = investments.map(investment => {
      const totalValue = Number(investment.totalCommitment ?? 0);
      const progressValue = Math.max(
         0,
         Math.min(Number(investment.capitalCalled ?? 0), totalValue),
      );

      return {
         ...investment,
         capitalCalled: progressValue,
         unfunded: totalValue - progressValue,
      };
   });
   const barSize = Math.max(
      24,
      Math.min(56, Math.floor(420 / Math.max(chartData.length, 1))),
   );
   const maxLabelLines = Math.max(
      1,
      ...chartData.map(
         investment =>
            wrapLabel(
               String(investment.account?.fund?.name ?? ''),
               X_AXIS_MAX_LINE_LENGTH,
            ).length,
      ),
   );
   const xAxisHeight = Math.max(60, maxLabelLines * 14 + 20);

   return (
      <div className="flex justify-center bg-white shadow overflow-hidden">
         <BarChart
            style={{
               width: '100%',
               maxWidth: '700px',
               maxHeight: '70vh',
               aspectRatio: 1.618,
            }}
            responsive
            data={chartData}
            margin={{
               top: 20,
               right: 0,
               left: 0,
               bottom: xAxisHeight,
            }}
         >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <Legend
               verticalAlign="top"
               align="center"
               iconType="square"
               wrapperStyle={{ paddingBottom: 20 }}
            />
            <XAxis
               dataKey="account.fund.name"
               interval={0}
               height={xAxisHeight}
               tick={<WrappedXAxisTick />}
            />
            <YAxis
               width="auto"
               tickFormatter={value => `$${formatNumber(value)}`}
            />
            <Tooltip content={<InvestmentsTooltipContent />} />
            <Bar
               dataKey="capitalCalled"
               name="Contributed"
               fill={calledColor}
               stackId="commitment"
               barSize={barSize}
               shape={props => (
                  <CylinderBarShape
                     x={props.x}
                     y={props.y}
                     width={props.width}
                     height={props.height}
                     color={calledColor}
                     transparent={false}
                     position={getCylinderPosition(
                        props.payload,
                        'capitalCalled',
                        STACK_KEYS,
                     )}
                  />
               )}
            />
            <Bar
               dataKey="unfunded"
               name="Unfunded"
               fill={CYLINDER_SHELL_COLOR}
               stackId="commitment"
               barSize={barSize}
               shape={props => (
                  <CylinderBarShape
                     x={props.x}
                     y={props.y}
                     width={props.width}
                     height={props.height}
                     color={CYLINDER_SHELL_COLOR}
                     transparent
                     position={getCylinderPosition(
                        props.payload,
                        'unfunded',
                        STACK_KEYS,
                     )}
                  />
               )}
            />
         </BarChart>
      </div>
   );
}
